import { COLOR_OPTION_KEYWORDS, } from '../variantTypes.js';
/** option1 … option3, indexed by PDP slot index (0-based). */
const VARIANT_SLOT_KEYS = [
    'option1',
    'option2',
    'option3',
];
/**
 * Maps option labels to variant fields. Shopify uses {@link VLPOption.position} as physical slot.
 * Placement can drop PDP rows without changing positions — keep using {@link VLPOption.position}.
 * When any {@link VLPOption.position} is negative (merged VP custom options), packed slots match
 * sort order by position ascending, not raw `position` as an index string.
 */
export const buildOptionKeyMap = (options) => {
    if (!options.length) {
        return {};
    }
    const map = {};
    if (options.some((opt) => opt.position < 0)) {
        const sorted = [...options].sort((a, b) => a.position - b.position);
        for (let i = 0; i < sorted.length; i++) {
            const slot = VARIANT_SLOT_KEYS[i];
            const row = sorted[i];
            if (slot != null && row != null) {
                map[row.name] = slot;
            }
        }
        return map;
    }
    for (const opt of options) {
        map[opt.name] = VARIANT_SLOT_KEYS[opt.position];
    }
    return map;
};
/**
 * PDP-coherent variant: every row in `optionInfo` has a non-empty slot matching a listed value.
 * Pass `optionKeyMap` when checking many variants.
 */
export function variantMatchesDisplayedOptionInfo(variant, optionInfo, optionKeyMap) {
    if (!optionInfo.length) {
        return true;
    }
    const keys = optionKeyMap !== null && optionKeyMap !== void 0 ? optionKeyMap : buildOptionKeyMap(optionInfo);
    return optionInfo.every((opt) => {
        const slot = keys[opt.name];
        if (!slot) {
            return false;
        }
        const raw = variant[slot];
        if (raw == null || raw === '') {
            return false;
        }
        const low = String(raw).trim().toLowerCase();
        return opt.values.some((val) => val.value.trim().toLowerCase() === low);
    });
}
/** Shopify IDs for variants coherent with placement-filtered {@link optionInfo}. */
export function displayedVariantShopifyIds(variants, optionInfo) {
    if (!optionInfo.length) {
        return new Set(variants.map((v) => v.shopifyId));
    }
    const optionKeyMap = buildOptionKeyMap(optionInfo);
    const ids = new Set();
    for (const v of variants) {
        if (variantMatchesDisplayedOptionInfo(v, optionInfo, optionKeyMap)) {
            ids.add(v.shopifyId);
        }
    }
    return ids;
}
// Core variant finding functions
export const findByShopifyId = (variants, variantId) => variantId ? variants.find((v) => v.shopifyId === variantId) : undefined;
/**
 * Selects a variant with fallback priority:
 * 1. Selected variant ID (if provided)
 * 2. Default variant ID (if provided)
 * 3. First variant
 * Returns null if no variants available
 */
export const selectVariantWithFallback = ({ variants, selectedVariantId, defaultVariantId, }) => {
    if (!(variants === null || variants === void 0 ? void 0 : variants.length))
        return null;
    // Priority 1: Selected variant
    if (selectedVariantId) {
        const selected = findByShopifyId(variants, selectedVariantId);
        if (selected)
            return selected;
    }
    // Priority 2: Default variant
    if (defaultVariantId) {
        const defaultVariant = findByShopifyId(variants, defaultVariantId);
        if (defaultVariant)
            return defaultVariant;
    }
    // Priority 3: First variant
    return variants[0];
};
/**
 * Returns initial selected options from variants/options using the same priority
 * as selectVariantWithFallback. When preferAvailable is true, if the chosen
 * variant is unavailable, the first available variant is used instead.
 */
export const getInitialSelectedOptions = ({ variants, options, optionKeyMap, selectedVariantId, defaultVariantId, preferAvailable = false, }) => {
    var _a;
    if (!variants.length || !options.length)
        return [];
    let variant = selectVariantWithFallback({
        variants,
        selectedVariantId,
        defaultVariantId,
    });
    if (!variant)
        return [];
    if (preferAvailable && !variant.available) {
        variant = (_a = variants.find((v) => v.available)) !== null && _a !== void 0 ? _a : variant;
    }
    return getOptionsFromVariant(variant, options, optionKeyMap);
};
/**
 * Resolves the active variant and its image from current selected options.
 * When no variant matches, falls back to the first variant. Optionally accepts
 * a fallback image URL when the variant has no featuredImage.
 */
export const getActiveVariantAndImage = (variants, selectedOptions, optionKeyMap, options) => {
    var _a, _b, _c, _d;
    const fallbackImage = (_a = options === null || options === void 0 ? void 0 : options.fallbackImageUrl) !== null && _a !== void 0 ? _a : null;
    const variant = (_b = findVariantByOptions(variants, selectedOptions, optionKeyMap)) !== null && _b !== void 0 ? _b : variants[0];
    return {
        activeVariant: variant !== null && variant !== void 0 ? variant : null,
        variantImage: (_d = (_c = variant === null || variant === void 0 ? void 0 : variant.featuredImage) !== null && _c !== void 0 ? _c : fallbackImage) !== null && _d !== void 0 ? _d : null,
    };
};
export const findVariantByOptions = (variants, selectedOptions, optionKeyMap) => {
    if (!variants.length || !selectedOptions.length)
        return undefined;
    return variants.find((variant) => selectedOptions.every((selected) => {
        // Empty value means the slot wasn't populated for the source variant
        // (e.g. a virtual product that merges a single-variant Shopify product
        // with a multi-variant one); treat it as "no constraint" to match the
        // semantics of isOptionAvailable.
        if (!selected.value)
            return true;
        const key = optionKeyMap[selected.name];
        return key ? variant[key] === selected.value : false;
    }));
};
// Option availability checking
export const isOptionAvailable = ({ variants, optionName, optionValue, selectedOptions, optionKeyMap, }) => {
    if (!selectedOptions.some((opt) => opt.value))
        return true;
    const optionKey = optionKeyMap[optionName];
    if (!optionKey)
        return false;
    return variants.some((variant) => {
        if (!variant.available)
            return false;
        if (variant[optionKey] !== optionValue)
            return false;
        return selectedOptions.every((selected) => {
            if (!selected.value || selected.name === optionName)
                return true;
            const key = optionKeyMap[selected.name];
            return key ? variant[key] === selected.value : false;
        });
    });
};
// Option management
export const getOptionsFromVariant = (variant, options, optionKeyMap) => {
    if (!options.length)
        return [];
    return options.map((opt) => {
        var _a;
        const key = optionKeyMap[opt.name];
        return {
            name: opt.name,
            value: (_a = (key ? variant[key] : null)) !== null && _a !== void 0 ? _a : '',
        };
    });
};
/**
 * Whether any of the first 3 option groups has more than one value,
 * i.e. the shopper has a meaningful choice to make.
 */
export const hasRealVariantSelection = (options) => {
    return (options !== null && options !== void 0 ? options : []).slice(0, 3).some((opt) => opt.values.length > 1);
};
const getDisplayableVariantOptions = (variant, options, optionKeyMap) => {
    var _a, _b;
    if (!variant)
        return null;
    const optionInfo = options.slice(0, 3);
    if (!hasRealVariantSelection(optionInfo)) {
        return null;
    }
    const selectedOptions = getOptionsFromVariant(variant, optionInfo, optionKeyMap).filter((_, idx) => { var _a, _b, _c; return ((_c = (_b = (_a = optionInfo[idx]) === null || _a === void 0 ? void 0 : _a.values) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 1; });
    return {
        selectedOptions,
        fallbackTitle: (_b = (_a = variant.title) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : '',
    };
};
/**
 * Human-readable variant label: `"Option: Value, ..."` for real multi-option products.
 * When there is no multi-value option (e.g. only "Default Title"), returns empty string.
 * Matches admin `DefaultVariantSelector` and storefront Buy Now CTA subtitle behavior.
 */
export const formatVariantDisplayName = (variant, options, optionKeyMap) => {
    const displayableOptions = getDisplayableVariantOptions(variant, options, optionKeyMap);
    if (!displayableOptions)
        return '';
    const parts = displayableOptions.selectedOptions.map((opt) => `${opt.name}: ${opt.value}`);
    if (parts.length === 0) {
        return displayableOptions.fallbackTitle;
    }
    return parts.join(', ');
};
/**
 * Compact selected option values for surfaces where option labels are too noisy.
 * Example: `"Forest Green · Carry-on"`.
 */
export const formatVariantOptionValues = (variant, options, optionKeyMap) => {
    const displayableOptions = getDisplayableVariantOptions(variant, options, optionKeyMap);
    if (!displayableOptions)
        return '';
    const parts = displayableOptions.selectedOptions
        .map((opt) => opt.value.trim())
        .filter(Boolean);
    if (parts.length === 0) {
        return displayableOptions.fallbackTitle;
    }
    return parts.join(' · ');
};
/** `formatVariantDisplayName`, trimmed, then `variantId` if empty. */
export const variantDisplayLabelOrId = (variant, options, optionKeyMap, variantId) => {
    const name = formatVariantDisplayName(variant, options, optionKeyMap).trim();
    return name || variantId;
};
/**
 * Resolves which default variant ID to use and identifies its source.
 * This centralizes the fallback priority logic used across the codebase:
 * 1. Storefront-level override (defaultVariantOverrideId)
 * 2. Product-level explicit default (defaultVariantId)
 * 3. Best selling variant (bestSellingVariantId)
 * 4. Fallback (first available / renderedDefaultVariantId)
 */
export const resolveDefaultVariantSource = (params) => {
    const { defaultVariantOverrideId, defaultVariantId, bestSellingVariantId, fallbackVariantId, } = params;
    if (defaultVariantOverrideId) {
        return { source: 'override', variantId: defaultVariantOverrideId };
    }
    if (defaultVariantId) {
        return { source: 'explicit', variantId: defaultVariantId };
    }
    if (bestSellingVariantId) {
        return { source: 'bestSelling', variantId: bestSellingVariantId };
    }
    if (fallbackVariantId) {
        return { source: 'fallback', variantId: fallbackVariantId };
    }
    return { source: null, variantId: null };
};
/**
 * Resolves to the first available variant from the priority list, checking availability.
 * Used by the backend to compute renderedDefaultVariantId.
 *
 * Priority: override → explicit → bestSelling → first available in list order.
 *
 * Non-empty {@link visibleVariantIds}: only those IDs are eligible. Pass `undefined` to skip
 * filtering (legacy callers). An empty set means no PDP-coherent variant rows.
 *
 * Empty variants array: falls back to the first candidate ID (link-level default) when Shopify
 * returned no variant rows. Otherwise empty eligible list yields null after visibility filtering.
 */
export const resolveAvailableDefaultVariant = (params) => {
    var _a;
    const { defaultVariantOverrideId, defaultVariantId, bestSellingVariantId, variants, visibleVariantIds, } = params;
    const candidateIds = [
        defaultVariantOverrideId,
        defaultVariantId,
        bestSellingVariantId,
    ].filter((id) => id != null && id !== '');
    if (!variants.length) {
        return (_a = candidateIds[0]) !== null && _a !== void 0 ? _a : null;
    }
    // Restrict the working set to visible variants when the caller supplies the set.
    // A variant whose option value was hidden by placement rules must not be selected
    // as the default, even if it is the highest-priority candidate — doing so causes
    // ATC / Buy Now to silently add the hidden-option variant.
    const eligibleVariants = visibleVariantIds === undefined
        ? variants
        : variants.filter((v) => visibleVariantIds.has(v.shopifyId));
    if (!eligibleVariants || eligibleVariants.length === 0) {
        // No visible variants at all — nothing safe to select.
        return null;
    }
    // Check each candidate in priority order: must be visible AND available.
    for (const id of candidateIds) {
        const variant = eligibleVariants.find((v) => v.shopifyId === id);
        if (variant === null || variant === void 0 ? void 0 : variant.available) {
            return id;
        }
    }
    // No priority candidate is available/visible.
    // eligibleVariants preserves the order returned by the caller (rank-sorted by the
    // backend), so the first available one is the best-selling visible option.
    const fallbackVariant = eligibleVariants.find((v) => v.available);
    if (fallbackVariant) {
        return fallbackVariant.shopifyId;
    }
    // All visible variants are unavailable — return the first visible one so the UI
    // shows a coherent (if OOS) state rather than an invisible variant.
    return eligibleVariants[0].shopifyId;
};
// Variant display helpers
export const getVariantImageForOption = ({ variants, optionName, optionValue, optionKeyMap, }) => {
    const optionKey = optionKeyMap[optionName];
    if (!optionKey)
        return null;
    const matchingVariant = variants.find((v) => v[optionKey] === optionValue);
    return (matchingVariant === null || matchingVariant === void 0 ? void 0 : matchingVariant.featuredImage) || null;
};
// Helper for checking unavailable options
export const hasUnavailableOptions = ({ options, variants, selectedOptions, currentOptionName, optionKeyMap, }) => options.some((otherOption) => otherOption.name !== currentOptionName &&
    otherOption.values.some(({ value }) => !isOptionAvailable({
        variants,
        optionName: otherOption.name,
        optionValue: value,
        selectedOptions,
        optionKeyMap,
    })));
export const hasSwatchData = (option) => option.values.some((v) => v.color !== undefined) ||
    option.values.some((v) => v.image !== undefined);
export const orderOptionsSwatchFirst = (options) => [
    ...options.filter((option) => hasSwatchData(option)),
    ...options.filter((option) => !hasSwatchData(option)),
];
// Helper for checking if an option is a color option
export const isColorOption = (option) => COLOR_OPTION_KEYWORDS.some((keyword) => option.name.toLowerCase().includes(keyword)) || hasSwatchData(option);
export const findFirstAvailableVariantWithOption = ({ variants, optionName, optionValue, optionKeyMap, }) => {
    const optionKey = optionKeyMap[optionName];
    if (!optionKey)
        return null;
    return (variants.find((variant) => variant.available && variant[optionKey] === optionValue) || null);
};
/**
 * Resolves the closest variant to "targetVariant" that is avaiable.
 * If the targetVariant is available, it will be returned.  Otherwise it will
 * find the first available variant of the same option value for the given optionName
 * (if present).  If no availble variants of that option name exists (or optionName is not given)
 * it will find the first avaialble variant with any options.
 * Falls back to returning the targetVariant if there are no availalble variants at all,
 * or if the variants list is empty.
 */
export const selectClosestAvailableVariant = ({ variants, options, targetVariant, preferredOptionName, }) => {
    var _a, _b, _c, _d, _e;
    if (!variants.length)
        return targetVariant;
    if (targetVariant === null || targetVariant === void 0 ? void 0 : targetVariant.available)
        return targetVariant;
    const keyMap = buildOptionKeyMap(options);
    const optionName = (_b = preferredOptionName !== null && preferredOptionName !== void 0 ? preferredOptionName : (_a = options[0]) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : null;
    const targetVariantOptions = targetVariant
        ? getOptionsFromVariant(targetVariant, options, keyMap)
        : [];
    const optionValue = (_d = (_c = targetVariantOptions.find((o) => o.name === optionName)) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : null;
    const firstWithPreferredOption = optionName && optionValue
        ? findFirstAvailableVariantWithOption({
            variants,
            optionName,
            optionValue,
            optionKeyMap: keyMap,
        })
        : null;
    const firstAvailable = variants.find((v) => v.available);
    return (_e = firstWithPreferredOption !== null && firstWithPreferredOption !== void 0 ? firstWithPreferredOption : firstAvailable) !== null && _e !== void 0 ? _e : targetVariant;
};
export const hasAnyAvailableVariantForOption = ({ variants, optionName, optionValue, optionKeyMap, }) => {
    return (findFirstAvailableVariantWithOption({
        variants,
        optionName,
        optionValue,
        optionKeyMap,
    }) !== null);
};
/**
 * For grouped products (virtual products / product groups), narrows the
 * variant + option lists to only the primary Shopify product's variants and
 * trims option values to those present in the filtered set. Non-grouped
 * products are returned unchanged.
 *
 * Falls back to the full lists when the primary product has no variants
 * (defensive — shouldn't happen in practice).
 */
export function primaryProductVariantsAndOptions(params) {
    const { variants, options, virtualProductId, productGroupKey, shopifyId } = params;
    const grouped = virtualProductId != null || Boolean(productGroupKey);
    if (!grouped) {
        return { variants, options };
    }
    const primaryVariants = variants.filter((v) => v.shopifyProductId === shopifyId);
    if (!primaryVariants.length) {
        return { variants, options };
    }
    const optionKeyMap = buildOptionKeyMap(options);
    const trimmedOptions = options
        .map((opt) => {
        const key = optionKeyMap[opt.name];
        if (!key)
            return opt;
        const presentValues = new Set(primaryVariants
            .map((v) => v[key])
            .filter((val) => val != null));
        const filteredValues = opt.values.filter((v) => presentValues.has(v.value));
        if (filteredValues.length === opt.values.length)
            return opt;
        return Object.assign(Object.assign({}, opt), { values: filteredValues });
    })
        .filter((opt) => opt.values.length > 0);
    return { variants: primaryVariants, options: trimmedOptions };
}
